<template>
  <div class="relative w-full h-full">
    <o-sidebar stored class="flex-1">
      <template v-if="controls" #left="{ toggle }">
        <div class="flex flex-col h-full">
          <div class="flex justify-between p-5">
            <div class="flex items-center">
              <h2 class="text-xl tracking-tight font-semibold">
                {{ $t('components.schedule.elements.controller.bookings') }}
              </h2>
            </div>
            <o-button
              class="relative md:hidden"
              icon="x"
              @click="toggle()"
            />
          </div>
          <div v-if="!isMonth" class="flex-none p-3 pt-0">
            <client-only>
              <schedule-calendar
                v-model="dates"
                :employees="employees"
                :options="options"
              />
            </client-only>
          </div>

          <schedule-reservations
            :bookings="bookings"
          />
        </div>
      </template>

      <template #right="{ state, toggle }">
        <div class="h-full max-h-full flex flex-col bg-white dark:bg-gray-900 relative">
          <!--
            Thin indeterminate progress bar — replaces the previous full-screen
            spinner overlay. Shows the view is fetching without blocking
            interaction or hiding the (usually cached) previous content. Match
            the UX pattern we use for the rest of the app's loading states
            (subtle top-level signal, don't cover the content).
          -->
          <transition name="fade">
            <div
              v-if="loading"
              class="schedule-progress pointer-events-none absolute top-0 left-0 right-0 h-0.5 z-70 overflow-hidden"
              aria-hidden="true"
            >
              <span class="schedule-progress-bar block h-full bg-primary-500 dark:bg-primary-400" />
            </div>
          </transition>

          <div v-if="controls" class="min-h-12 w-full px-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 py-2">
            <div v-if="provider" class="w-full flex flex-col sm:flex-row sm:flex-wrap items-center justify-between">
              <div class="flex-shrink hidden sm:inline">
                <div class="flex items-center justify-left my-1">
                  <div
                    :class="[
                      state.collapse ? 'sm:mr-4' : 'sm:-ml-4 sm:mr-4'
                    ]"
                  >
                    <button
                      :class="[
                        'relative border p-3 sm:p-2 h-9 flex items-center justify-center bg-gray-50 dark:bg-gray-900 dark:border-gray-800',
                        state.collapse ? 'rounded-md' : '-ml-px border sm:border-l-0 rounded-md sm:rounded-l-none'
                      ]"
                      @click="toggle()"
                    >
                      <o-icon v-if="!state.collapse" icon="chevronLeft" :size="9" class="mr-1 text-gray-700 sm:inline-block mt-px hidden" />
                      <o-icon icon="filter" :size="12" class="text-gray-600" />
                      <o-icon v-if="state.collapse" icon="chevronRight" :size="9" class="ml-1 text-gray-700 sm:inline-block mt-px hidden" />
                    </button>
                  </div>

                  <div class="hidden md:inline-flex mr-2">
                    <schedule-employee-controls
                      v-model="employees"
                      :options="options"
                    />
                  </div>

                  <div class="w-24 hidden md:inline-flex">
                    <schedule-view-controls />
                  </div>
                </div>
              </div>

              <div class="w-full sm:w-auto flex-grow flex-shrink-0 order-3 sm:order-none">
                <div class="w-full sm:w-auto flex items-center justify-center my-1">
                  <schedule-date-controls
                    v-model:dates="dates"
                    :options="options"
                    :loading="loading"
                    @refresh="handleRefresh"
                  />
                </div>
              </div>

              <div class="w-full sm:w-auto flex justify-between items-center">
                <o-button
                  flat
                  class="sm:hidden"
                  icon="filter"
                  @click="toggle()"
                />
                <div class="flex items-center justify-right my-1 space-x-2">
                  <o-tooltip :text="privacyMode ? $t('components.schedule.elements.controller.disablePrivacyMode') : $t('components.schedule.elements.controller.enablePrivacyMode')">
                    <o-button :icon="privacyMode ? 'locked' : 'unlock'" flat @click="togglePrivacyMode" />
                  </o-tooltip>

                  <o-tooltip :text="$t('components.schedule.elements.controller.exportJPG')">
                    <o-button icon="export" flat @click="handleExport()" />
                  </o-tooltip>

                  <o-tooltip :text="$t('general.print')">
                    <o-button icon="print" flat @click="handlePrint()" />
                  </o-tooltip>

                  <o-tooltip :text="$t('general.waitlist')">
                    <o-panel-link
                      tag="o-button"
                      flat
                      path="/waitlist"
                      icon="waitlist"
                    />
                  </o-tooltip>

                  <o-button
                    flat
                    class="inline-flex"
                    icon="ellipses"
                    @click="handleSettings"
                  />

                  <div class="flex items-center divide-x">
                    <o-panel-link
                      tag="o-button"
                      path="/appointment/create"
                      :props="{ presets: { date: $date.parse(dates[0]) } }"
                      variant="primary"
                      rounded="rounded-l-md"
                      class="hidden xl:inline-flex"
                    >
                      <p class="hidden 2xl:block">
                        {{ $t('reservation.new') }}
                      </p>
                      <p class="block 2xl:hidden">
                        {{ $t('general.new') }}
                      </p>
                    </o-panel-link>
                    <o-dropdown placement="bottom-end">
                      <o-button
                        variant="primary"
                        rounded="rounded-md xl:rounded-l-none xl:rounded-r-md"
                        size="slim"
                      >
                        <o-icon icon="chevronDown" :size="12" class="hidden xl:block px-px" />
                        <o-icon icon="add" :size="12" class="block xl:hidden px-px" />
                      </o-button>

                      <template #content>
                        <o-dropdown-item
                          :name="$t('general.appointment')"
                          :description="$t('schedule.createNewCustomerAppointment')"
                          tag-name="o-panel-link"
                          path="/appointment/create"
                          :props="{ presets: { date: $date.parse(dates[0]) } }"
                        />
                        <o-dropdown-item
                          :name="$t('general.class')"
                          :description="$t('schedule.createNewClass')"
                          tag-name="o-panel-link"
                          path="/appointment/create?type=group"
                          :props="{ presets: { date: $date.parse(dates[0]) } }"
                        />
                        <o-dropdown-item
                          v-if="$gates.get('courses') && $grants.all({ customerCourse: ['create'] })"
                          name="Course"
                          description="Enrol customer in a course"
                          tag-name="o-panel-link"
                          path="/customer/course/add"
                        />
                        <o-dropdown-item
                          :name="$t('schedule.breakTask')"
                          :description="$t('schedule.breakTaskInfo')"
                          @click="handleCreateBreak({ date: $date.parse(dates[0]) })"
                        />
                      </template>
                    </o-dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-if="provider">
            <slot v-bind="controllerProps" />
          </template>
        </div>
      </template>
    </o-sidebar>

    <div id="schedule" />

    <settings-modal
      ref="settingsModal"
    />

    <edit-break-dialog
      ref="editBreakDialog"
    />

    <create-break-dialog
      ref="createBreakDialog"
      @success="handleRefresh"
    />

    <privacy-mode
      :active="privacyMode"
      @disable="togglePrivacyMode"
    />
  </div>
</template>

<script setup lang="ts">
// snapdom is ~100KB and only used by handleExport() below. See .docs/performance.md.
// Loaded dynamically inside the handler to keep it off the schedule critical path.

import { Provider } from '@/components/schedule/provider/provider'

import ScheduleCalendar from '@/components/schedule/elements/Calendar.vue'
import ScheduleReservations from '@/components/schedule/elements/Reservations.vue'
import ScheduleViewControls from '@/components/schedule/elements/ViewControls.vue'
import ScheduleDateControls from '@/components/schedule/elements/DateControls.vue'
import ScheduleEmployeeControls from '@/components/schedule/elements/EmployeeControls.vue'
import PrivacyMode from '@/components/elements/PrivacyMode.vue'

import SettingsModal from '@/components/schedule/actions/Settings.vue'
import EditBreakDialog from '@/components/schedule/actions/EditBreak.vue'
import CreateBreakDialog from '@/components/schedule/actions/CreateBreak.vue'

import EmployeeSelect from '@/components/employee/elements/Select.vue'

import { OSidebar, ODropdownItem } from '@/components/elements'

import { useUIStore } from '~/stores/useUIStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { useScheduleCacheStore } from '~/stores/useScheduleCacheStore'

const { $api, $auth, $date, $dateFns, $bus, $notification, $print } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const instance = getCurrentInstance()
const uiStore = useUIStore()
const employeeStore = useEmployeeStore()

const validSchedulePaths = new Set([
  '/schedule/day',
  '/schedule/week',
  '/schedule/month',
  '/schedule/timetable',
  '/schedule/grid',
])

interface Props {
  options: any
  controls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  controls: true,
})

const emit = defineEmits<{
  (e: 'provider', value: any): void
}>()

const settingsModal = ref<any>()
const editBreakDialog = ref<any>()
const createBreakDialog = ref<any>()

const provider = ref<any>(null)
const bookings = ref<any>(null)
const loading = ref(false)
const downloadImageUrl = ref<string | null>(null)

const controllerProps = computed(() => ({
  provider: provider.value,
  loading: loading.value,
}))

const privacyMode = computed(() => uiStore.privacyMode)

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

function parseScheduleQueryDate(value: unknown): Date {
  const rawValue = Array.isArray(value) ? value[0] : value

  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return new Date()
  }

  const parsed = /^\d{8}$/.test(rawValue)
    ? $date.parse(rawValue)
    : new Date(rawValue)

  if (isValidDate(parsed)) {
    return parsed
  }

  return new Date()
}

function normalizeScheduleQueryDate(value: unknown): string | null {
  const rawValue = Array.isArray(value) ? value[0] : value
  const candidate = rawValue && typeof rawValue === 'object'
    ? (rawValue as Record<string, unknown>).start ?? (rawValue as Record<string, unknown>).end ?? null
    : rawValue

  if (candidate instanceof Date) {
    return isValidDate(candidate) ? $date.format(candidate) : null
  }

  if (typeof candidate === 'string') {
    if (/^\d{8}$/.test(candidate)) {
      return candidate
    }

    const parsed = new Date(candidate)
    return isValidDate(parsed) ? $date.format(parsed) : null
  }

  return null
}

const dates = computed({
  get() {
    const query = route.query
    const date = parseScheduleQueryDate(query.date)
    const options = props.options
    const mode = options.date.mode

    const dates: string[] = []

    if (mode === 'range') {
      // startDayOfWeek is the 1-7 StartDayOfWeek enum (Mon=1..Sun=7);
      // convert to the 0-6 scheme date-fns uses (Sun=0..Sat=6).
      const day = $auth?.info?.location?.startDayOfWeek ?? 1
      const weekStartsOn = day === 7 ? 0 : day
      let range = $dateFns.startOfWeek(date, { weekStartsOn })

      while (dates.length < 7) {
        dates.push($date.format(range))
        range = $date.add(range, { days: 1 })
      }
    } else if (mode === 'month') {
      const start = $dateFns.startOfMonth(date)
      const end = $dateFns.endOfMonth(date)

      let range = start

      while (range <= end) {
        dates.push($date.format(range))
        range = $date.add(range, { days: 1 })
      }
    } else {
      dates.push($date.format(date))
    }

    return dates
  },
  set(value: any[]) {
    const path = getSafeSchedulePath()
    const query = route.query
    const date = normalizeScheduleQueryDate(value)

    if (date && date !== query.date) {
      router.push({
        path,
        query: Object.assign({}, query, { date }),
      })
    }
  },
})

const employees = computed({
  get() {
    const query = route.query
    const options = props.options

    if (query.employees || options.employee.required) {
      const employeesList = employeeStore.employeesTypeEmployee

      if (query.employees) {
        const ids = String(route.query.employees).split(',').map(Number)
        const employees = employeesList.filter(({ idEmployee }: any) => ids.includes(idEmployee))

        if (employees) {
          return employees
        }
      }

      if (options.employee.required && employeesList.length) {
        return [employeesList[0]]
      }
    }

    return []
  },
  set(value: any[]) {
    console.log(value)
    const path = getSafeSchedulePath()
    const query = route.query

    const employees = value.map(({ idEmployee }: any) => idEmployee).join()

    router.push({
      path,
      query: Object.assign({}, query, { employees }),
    })
  },
})

function getSafeSchedulePath() {
  const path = route.path
  if (validSchedulePaths.has(path)) {
    return path
  }

  return '/schedule/day'
}

const isMonth = computed(() => props.options.date.mode === 'month')

watch(provider, (val) => {
  emit('provider', val)
}, { deep: true })

watch(dates, (newDates, oldDates) => {
  if (newDates.join('|') !== oldDates.join('|')) {
    getBookings()
  }
})

watch(employees, (newEmployees, oldEmployees) => {
  const newEmployeeIDs = newEmployees.map(({ idEmployee }: any) => idEmployee).join()
  const oldEmployeeIDs = oldEmployees.map(({ idEmployee }: any) => idEmployee).join()

  if (newEmployeeIDs !== oldEmployeeIDs) {
    getBookings()
  }
})

onMounted(async () => {
  await employeeStore.sync()
  await getBookings()

  const uid = instance?.uid

  $bus.on.all('Reservation', uid, (reservation: any) => {
    const d = dates.value
    const current = d.includes(reservation?.date?.value?.toString()) || d.includes(reservation?.date?.toString())

    if (current) {
      getBookings({ loader: false })
    }
  })

  $bus.on.all('EmployeeBreak', uid, (employeeBreak: any) => {
    const d = dates.value
    const current = d.includes(employeeBreak?.date?.value?.toString())

    if (current) {
      getBookings({ loader: false })
    }
  })
})

onBeforeUnmount(() => {
  $bus.off.all(instance?.uid)
})

function togglePrivacyMode() {
  uiStore.togglePrivacyMode()
}

// Stale-then-refresh booking cache lives in `useScheduleCacheStore` so it
// survives this component unmounting (e.g. you leave /schedule and come back).
// See app/stores/useScheduleCacheStore.ts for scope/eviction notes.
const scheduleCache = useScheduleCacheStore()

function getBookingsCacheKey(accountId: string, api: string, d: string[], employeeIds: string[]) {
  // accountId is part of the key so cached responses can never bleed between
  // signed-in accounts (the switcher can change `$auth.active.guid` mid-session
  // without a full page reload). The store also wipes the cache on account
  // change as a hygiene measure, but the keying is the correctness backstop:
  // even if a cache entry survived a switch, it could never match.
  return `${accountId}|${api}|${d.join('|')}|${employeeIds.join(',')}`
}

// Identifies the most recently-issued request. If a slower fetch for a prior
// key resolves after a faster fetch for the new key, we ignore it — otherwise
// rapidly switching dates could leave you looking at the wrong day's data.
// Component-instance scope is correct here (a stale request from a previous
// mount can't beat a current request from this mount).
let currentBookingsRequestKey = ''

interface GetBookingsOptions {
  loader?: boolean
  forceLoading?: boolean
}

async function getBookings({ loader = true, forceLoading = false }: GetBookingsOptions = {}) {
  const options = props.options
  const d = dates.value
  const e = employees.value
  const employeeIds = (e || []).map((emp: any) => String(emp.idEmployee))
  const accountId = ($auth as any).active?.guid ?? ''

  const apiMethod = getBookingApiMethod(options.api)
  const key = getBookingsCacheKey(accountId, apiMethod, d, employeeIds)
  currentBookingsRequestKey = key

  // Cache hit — paint immediately from cached data, suppress spinner.
  // Network refresh still fires below so the view catches up with any
  // changes since the entry was cached.
  const cached = scheduleCache.get(key)
  if (cached) {
    bookings.value = cached
    provider.value = new Provider(cached, { employees: e, ...options })
    loading.value = forceLoading
  } else {
    loading.value = loader
  }

  try {
    const response = await ($api.booking as any)[apiMethod](d, employeeIds)

    // Another newer request has started before us finished — drop this
    // result to avoid clobbering the current view.
    if (currentBookingsRequestKey !== key) return

    const b = response.data
    const p = new Provider(b, { employees: e, ...options })

    bookings.value = b
    provider.value = p
    scheduleCache.set(key, b)
  } catch (err: any) {
    console.log(err)
    if (currentBookingsRequestKey !== key) return
    $notification.error({ errors: err?.errors })
  } finally {
    if (currentBookingsRequestKey === key) loading.value = false
  }
}

function getBookingApiMethod(api: unknown): 'reservations' | 'timetable' {
  if (typeof api === 'string' && api === 'timetable') {
    return 'timetable'
  }

  if (api && typeof api === 'object') {
    const source = api as Record<string, unknown>
    const value = source.value ?? source.api
    if (value === 'timetable') {
      return 'timetable'
    }
  }

  return 'reservations'
}

function handleCreateBreak(options: any) {
  createBreakDialog.value.open(options)
}

function handleEditBreak(block: any) {
  editBreakDialog.value.open(block)
}

function handleSettings() {
  settingsModal.value.open()
}

function handleRefresh() {
  return getBookings({ forceLoading: true })
}

function handlePrint() {
  const calendar = document.getElementById('calendar')
  const content = calendar?.querySelector<HTMLElement>('.capture')

  if (content) {
    $print(content.outerHTML)
  }
}

async function handleExport() {
  const el = document.querySelector('.capture')

  if (el) {
    function watermarkOverlayPlugin(kind = 'locale') {
      return {
        name: 'timestamp-overlay',
        async afterClone(ctx: any) {
          console.log(ctx)
          const root = ctx.clone; if (!(root instanceof HTMLElement)) { return }
          const doc = root.ownerDocument || document
          const cs = getComputedStyle(root)
          if (!/relative|absolute|fixed|sticky/.test(cs.position)) { root.style.position = 'relative' }

          const tag = doc.createElement('div')
          tag.textContent = 'Powered by Ovatu'

          Object.assign(tag.style, {
            position: 'absolute',
            left: '40px',
            bottom: '20px',
            background: 'rgba(0,0,0,.70)',
            color: '#fff',
            padding: '.6rem 1rem',
            borderRadius: '6px',
            font: '16px system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif',
            letterSpacing: '.3px',
            lineHeight: '1',
            pointerEvents: 'none',
            backdropFilter: 'blur(1px)',
            WebkitBackdropFilter: 'blur(1px)',
            willChange: 'transform',
            transform: 'translateZ(0)',
            mixBlendMode: 'normal',
            textRendering: 'optimizeLegibility',
            zIndex: '9999999999999'
          })

          tag.style.setProperty('color', '#fff', 'important')
          tag.style.setProperty('-webkit-text-fill-color', '#fff', 'important')
          tag.style.setProperty('-webkit-text-stroke-width', '0', 'important')
          tag.style.setProperty('-webkit-text-stroke-color', 'transparent', 'important')
          root.appendChild(tag)
        }
      }
    }

    const { snapdom } = await import('@zumer/snapdom')

    const result = await snapdom(el, {
      debug: true,
      cache: 'disabled',
      format: 'jpg',
      filename: 'schedule_export',
      exclude: ['.reservation-block-status', '.reservation-block-notes', '.reservation-block-price', '.reservation-block-label', '.reservation-block-birthday', 'reservation-block-customer'],
      excludeMode: 'remove',
      fallbackURL: '/images/placeholder.png',
      plugins: [
        [watermarkOverlayPlugin],
      ]
    })

    console.log(result)

    await result.download({ format: 'jpg', filename: 'schedule_export' })
  }
}

provide('handleCreateBreak', handleCreateBreak)
provide('handleEditBreak', handleEditBreak)
</script>

<style scoped>
/*
 * Indeterminate progress bar — animated band that slides across the full
 * width, repeats while the `loading` flag is true. Rough match for the
 * modern top-nav pattern (YouTube/GitHub) but scoped to the schedule panel.
 * Replaces the full-screen overlay that blocked all interaction during
 * fetch.
 */
.schedule-progress-bar {
  width: 40%;
  animation: schedule-progress-slide 1.2s ease-in-out infinite;
  transform: translateX(-100%);
  will-change: transform;
}

@keyframes schedule-progress-slide {
  0%   { transform: translateX(-100%); }
  50%  { transform: translateX(180%); }
  100% { transform: translateX(260%); }
}
</style>
